import { useParams } from 'react-router-dom'
import { useContext, useEffect, useState } from 'react'
import SelectInput from '../../../../common/SelectInput'
import { getRequest } from '../../../../../services/base'
import useDebouncedValue from '../../../../../utils/common_function'
import Button from '../../../../common/Button'
import {
  DtcDependencyContext,
  SHOW_DEPENDENCY_MODAL,
} from '../../DtcEdit/context/context'
import { Flex, Form, Modal, Tooltip, Typography } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'

const DtcDependencyForm = ({ dependentDtcs, submitDependentDtc }) => {
  const { vehicle } = useParams()
  const intl = useIntl()
  const [state, dispatch] = useContext(DtcDependencyContext)
  const [form] = Form.useForm()

  const [options, setOptions] = useState([])
  const [searchFailed, setSearchFailed] = useState(false)
  const [searchDtc, setSearchDtc] = useState('')
  const debouncedSearchTerm = useDebouncedValue(searchDtc, 1000)

  useEffect(() => {
    getDtcsData()
  }, [])
  useEffect(() => {
    if (!state.show_dependency_modal) return

    form.resetFields()
    resetDtcSearch()
  }, [state.show_dependency_modal])

  useEffect(() => {
    getDtcsData(debouncedSearchTerm)
  }, [debouncedSearchTerm])

  const getDtcsData = async (search = '') => {
    let res = await getRequest(`/api/dtcs/?vehicle=${vehicle}&search=${encodeURIComponent(search)}`)
    // Search-as-you-type: shown in the dropdown, not as a toast per keystroke.
    if (res?.status === false) {
      setSearchFailed(true)
      setOptions([])
      return
    }
    setSearchFailed(false)
    // The result needs to include "value" required by Antd Select, so mapping it to option "id"
    const result = (res?.results || []).map((option, index) => {
      return {
        ...option,
        value: option?.id,
        label: option?.name,
      }
    })
    setOptions(result)
  }

  // Resets the search box back to the full unfiltered list, so a leftover search term
  // never resurfaces as a stale filtered list the next time the select/modal is opened.
  const resetDtcSearch = () => {
    setSearchDtc('')
    getDtcsData()
  }

  const AddDependencyForm = (
    <Form
      id="dtc-add-dependency-from"
      name="dtc-add-dependency-form"
      form={form}
      layout="vertical"
      onFinish={submitDependentDtc}
    >
      <Flex gap={16} justify="space-between">
        <SelectInput
          name="dtcs"
          label={intl.formatMessage({ id: 'dtcs.dependencies.selectDependentDtc' })}
          hideLabel={true}
          isMulti={true}
          isRequired={false}
          wrapperClass="select_dependant_dtc"
          options={options}
          onInputChange={(value) => {
            setSearchDtc(value)
          }}
          notFoundContent={
            searchFailed ? (
              <Typography.Text type="danger" style={{ color: 'var(--color-form-link-danger)' }}>
                {intl.formatMessage({ id: 'generic.searchFailed' })}
              </Typography.Text>
            ) : undefined
          }
          onClear={resetDtcSearch}
          onMenuClose={resetDtcSearch}
          maxTagCount={12}
          maxTagPlaceholder={(omittedValues) => (
            <Tooltip title={omittedValues.map((v) => v.label).join(', ')}>
              <span>+{omittedValues.length}</span>
            </Tooltip>
          )}
          formatOption={(option) => (
            <>
              <div className="option-label">
                {option.name} ({option.ecu})
              </div>
              <div className="option-description">{option.description}</div>
            </>
          )}
          formatLabel={(option) => {
            return (
              <>
                <div className="option-label">
                  {option.name} ({option.ecu})
                </div>
                <div className="option-description">{option.description}</div>
              </>
            )
          }}
          filterOption={() => true}
        />
      </Flex>
    </Form>
  )

  const handleOk = async () => {
    const values = await form.validateFields()
    submitDependentDtc(values)
  }

  const handleCancel = () => {
    dispatch({ type: SHOW_DEPENDENCY_MODAL, data: false })
  }

  return (
    <Modal
      title={
        <>
          <ExclamationCircleOutlined /> {intl.formatMessage({ id: 'dtcs.dependencies.addDependencies' })}
        </>
      }
      open={state.show_dependency_modal}
      okText={intl.formatMessage({ id: 'dtcs.dependencies.saveDependency' })}
      onOk={handleOk}
      onCancel={handleCancel}
      maskClosable={false}
      keyboard={false}
      centered
      width="50%"
    >
      {AddDependencyForm}
    </Modal>
  )
}

export default DtcDependencyForm
